import { sql } from "drizzle-orm";

import type { AccountEventRequest, WsEvent } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("query");

import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { accountEvent } from "@/db/schema";
import { AccountEvent } from "@/db/types";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";

const accountEventConflictTarget = [
  accountEvent.feedId,
  accountEvent.contentId,
  accountEvent.itemId,
  accountEvent.name,
  accountEvent.accountId,
];

const accountEventConflictSet = {
  createdAt: sql`"excluded"."createdAt"`,
  memberAvailability: sql`"excluded"."memberAvailability"`,
  deviceVolume: sql`"excluded"."deviceVolume"`,
};

export async function upsertAccountEvent(e: AccountEventRequest, deferEmit = false): Promise<void> {
  if (!e) {
    log.debug("upsertAccountEvent: no event");
    return;
  }

  const ae: AccountEvent = {
    id: e.id,
    accountId: e.accountId,
    feedId: e.feedId,
    createdAt: e.timestamp,
    contentId: e.contentId,
    itemId: e.feedItemId,
    name: e.name,
    memberAvailability: "",
    deviceVolume: 0,
  };

  await db
    .insert(accountEvent)
    .values(ae)
    .onConflictDoUpdate({
      target: accountEventConflictTarget,
      set: accountEventConflictSet,
    })
    .execute();
  if (!deferEmit) {
    emitTableUpdates(["account_event"]);
  }
}

export async function bulkUpsertAccountEvent(records: WsEvent[], deferEmit = false) {
  if (records.length === 0) {
    return;
  }
  const data = records
    .map((r) => ({
      id: r.id,
      accountId: r.accountId,
      contentId: r.contentId,
      createdAt: r.createdAt,
      deviceVolume: r.deviceVolume,
      feedId: r.feedId,
      itemId: r.itemId,
      memberAvailability: r.memberAvailability,
      name: r.name,
    }))
    .sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""));
  const dedupedById = deduplicateByKeyValue(data, (item) => item.id);
  const deduped = deduplicateByKeyValue(
    dedupedById,
    (item) => `${item.feedId}_${item.contentId}_${item.itemId}_${item.name}_${item.accountId}`,
  );
  await bulkInsert(accountEvent, deduped, {
    onConflict: {
      type: "doUpdate",
      target: accountEventConflictTarget,
      set: accountEventConflictSet,
    },
  });
  if (!deferEmit) {
    emitTableUpdates(["account_event"]);
  }
}
